import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Difficulty, MCQ, McqImageFile, McqOptionMedia, SubjectKey, SUBJECT_KEYS, normalizeMcqDifficulty, normalizeMcqSubjectKey } from '../lib/mcq';
import { apiRequest, buildSseStreamUrl, ensureStudentBearerTokenFromRefresh, resolveLaunchAuthToken } from '../lib/api';
import {
  formatStudentTokenDebugPreview,
  isCookieSessionApiMarker,
  readPersistedStudentAccessToken,
  resolveSnapshotStudentAuthToken,
} from '../lib/authSession';
import { waitUntilAuthHydrated, waitUntilClientAuthToken } from '../lib/authTiming';
import { cacheLaunchedExamSession } from '../lib/examWindowLaunch';
import { isNativeRuntime, logNativeEvent } from '../lib/nativeDiagnostics';
import { consumeBriefNativeHide, markNativeDocumentHidden } from '../lib/nativeForeground';
import { useAuth } from './AuthContext';

interface TestAttempt {
  id: string;
  sessionId?: string;
  subject: SubjectKey;
  topic: string;
  difficulty: Difficulty;
  score: number;
  totalQuestions: number;
  durationMinutes: number;
  attemptedAt: string;
  mode: 'topic' | 'mock' | 'adaptive';
  correctAnswers?: number;
  wrongAnswers?: number;
  unanswered?: number;
  submittedAnswers?: number;
  submittedAt?: string;
  metadata?: Record<string, unknown>;
}

interface SessionQuestion {
  id: string;
  subject: SubjectKey;
  topic: string;
  question: string;
  options: string[];
  optionMedia?: McqOptionMedia[];
  questionImage?: McqImageFile | null;
  difficulty: Difficulty;
  explanation?: string;
  explanationImage?: McqImageFile | null;
  shortTrick?: string;
  shortTrickImage?: McqImageFile | null;
}

interface TestReviewRow {
  questionId: string;
  question: string;
  questionImage?: McqImageFile | null;
  optionMedia: McqOptionMedia[];
  selectedKey: string | null;
  correctKey: string;
  selectedText?: string;
  correctText?: string;
  isCorrect: boolean;
  explanationText?: string;
  explanationImage?: McqImageFile | null;
  shortTrickText?: string;
  shortTrickImage?: McqImageFile | null;
}

interface SubmitTestSessionResult {
  attempt: TestAttempt;
  review?: TestReviewRow[];
}

interface TestSession {
  id: string;
  userId?: string;
  subject: SubjectKey;
  difficulty: Difficulty;
  topic: string;
  mode: 'topic' | 'mock' | 'adaptive';
  questionCount: number;
  durationMinutes: number;
  startedAt: string;
  finishedAt: string | null;
  questions: SessionQuestion[];
  netType?: string;
  testType?: string;
  config?: Record<string, unknown>;
}

interface ProfileState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  targetProgram: string;
  testSeries: string;
  sscPercentage: string;
  hsscPercentage: string;
  testDate: string;
}

interface PreferencesState {
  emailNotifications: boolean;
  dailyReminders: boolean;
  performanceReports: boolean;
  contentUpdates: boolean;
  notificationPreferences?: Record<string, boolean>;
}

interface AppDataContextValue {
  loading: boolean;
  error: string | null;
  mcqs: MCQ[];
  mcqTotalsBySubject: Record<SubjectKey, number>;
  mcqsBySubject: Record<SubjectKey, MCQ[]>;
  mcqsBySubjectAndDifficulty: Record<SubjectKey, Record<Difficulty, MCQ[]>>;
  attempts: TestAttempt[];
  profile: ProfileState;
  preferences: PreferencesState;
  startPracticeTest: (params: {
    subject: SubjectKey;
    difficulty: Difficulty;
    topic: string;
    mode: 'topic' | 'mock' | 'adaptive';
    questionCount?: number;
  }) => Promise<TestAttempt | null>;
  startTestSession: (params: {
    subject: SubjectKey;
    difficulty: Difficulty;
    topic: string;
    mode: 'topic' | 'mock' | 'adaptive';
    questionCount?: number;
    part?: string;
    chapter?: string;
    section?: string;
    netType?: string;
    testType?: 'subject-wise' | 'full-mock' | 'adaptive';
    selectedSubject?: SubjectKey;
    /** When set (e.g. from resolveLaunchAuthToken), used for /api/tests/start so mobile Safari is not stuck with stale React context vs localStorage. */
    authTokenHint?: string | null;
  }) => Promise<TestSession>;
  getTestSession: (sessionId: string) => Promise<TestSession>;
  submitTestSession: (params: {
    sessionId: string;
    answers: Array<{ questionId: string; selectedOption: string | null }>;
    elapsedSeconds: number;
  }) => Promise<SubmitTestSessionResult>;
  saveProfile: (partial: Partial<ProfileState>) => Promise<void>;
  savePreferences: (partial: Partial<PreferencesState>) => Promise<void>;
  refreshAttempts: () => Promise<void>;
}

const defaultProfile: ProfileState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  city: '',
  targetProgram: '',
  testSeries: '',
  sscPercentage: '',
  hsscPercentage: '',
  testDate: '',
};

const defaultPreferences: PreferencesState = {
  emailNotifications: true,
  dailyReminders: true,
  performanceReports: true,
  contentUpdates: true,
  notificationPreferences: {},
};

function normalizeSharedPreferences(preferences: PreferencesState): PreferencesState {
  const nested = preferences.notificationPreferences || {};
  const enabled = typeof nested.enabled === 'boolean' ? nested.enabled : preferences.emailNotifications !== false;
  const newVideos = typeof nested.newVideos === 'boolean'
    ? nested.newVideos
    : typeof nested.videoUploads === 'boolean'
      ? nested.videoUploads
      : preferences.contentUpdates !== false;
  const dailyReminders = typeof nested.dailyReminders === 'boolean' ? nested.dailyReminders : preferences.dailyReminders !== false;
  const performanceReports = typeof nested.performanceReports === 'boolean' ? nested.performanceReports : preferences.performanceReports !== false;
  return {
    emailNotifications: enabled,
    dailyReminders,
    performanceReports,
    contentUpdates: newVideos,
    notificationPreferences: {
      ...nested,
      enabled,
      newVideos,
      videoUploads: newVideos,
      dailyReminders,
      performanceReports,
    },
  };
}

const AppDataContext = createContext<AppDataContextValue | undefined>(undefined);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const { token, user, loading: authLoading } = useAuth();
  const authLoadingRef = useRef(authLoading);
  authLoadingRef.current = authLoading;

  const resolveClientAuthToken = useCallback((): string | null => {
    return resolveSnapshotStudentAuthToken(token, user);
  }, [token, user]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mcqs, setMcqs] = useState<MCQ[]>([]);
  const [mcqTotalsBySubject, setMcqTotalsBySubject] = useState<Record<SubjectKey, number>>(
    Object.fromEntries(SUBJECT_KEYS.map((subject) => [subject, 0])) as Record<SubjectKey, number>,
  );
  const [attempts, setAttempts] = useState<TestAttempt[]>([]);
  const [profile, setProfile] = useState<ProfileState>(defaultProfile);
  const [preferences, setPreferences] = useState<PreferencesState>(defaultPreferences);
  const syncInFlightRef = useRef(false);
  const syncQueuedRef = useRef(false);
  const syncDebounceTimerRef = useRef<number | null>(null);
  /** Coalesces overlapping /api/mcqs loads (login effect + foreground sync) onto one in-flight request. */
  const mcqDataLoadPromiseRef = useRef<Promise<void> | null>(null);

  const applyUserPayload = useCallback((userData: ProfileState & { preferences: PreferencesState }) => {
    setProfile({
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      phone: userData.phone || '',
      city: userData.city || '',
      targetProgram: userData.targetProgram || '',
      testSeries: userData.testSeries || '',
      sscPercentage: userData.sscPercentage || '',
      hsscPercentage: userData.hsscPercentage || '',
      testDate: userData.testDate || '',
    });
    setPreferences(normalizeSharedPreferences(userData.preferences || defaultPreferences));
  }, []);

  const loadMcqData = useCallback(async () => {
    if (mcqDataLoadPromiseRef.current) {
      return mcqDataLoadPromiseRef.current;
    }
    const promise = (async () => {
      const refreshTag = Date.now();
      const [payload, countPayload] = await Promise.all([
        apiRequest<{ mcqs: MCQ[] }>(`/api/mcqs?t=${refreshTag}`, { cache: 'no-store', retryCount: 1 }),
        apiRequest<{ counts: Partial<Record<SubjectKey, number>> }>(`/api/mcqs/counts?t=${refreshTag}`, { cache: 'no-store', retryCount: 1 }),
      ]);
      setMcqs(payload.mcqs || []);
      setMcqTotalsBySubject(
        SUBJECT_KEYS.reduce((acc, subject) => {
          acc[subject] = Number(countPayload?.counts?.[subject] || 0);
          return acc;
        }, {} as Record<SubjectKey, number>),
      );
    })().finally(() => {
      mcqDataLoadPromiseRef.current = null;
    });
    mcqDataLoadPromiseRef.current = promise;
    return promise;
  }, []);

  const loadUserData = useCallback(async (authToken: string, silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const mePayload = await apiRequest<{ user: ProfileState & { preferences: PreferencesState } }>('/api/auth/me', {}, authToken);
      const attemptsPayload = await apiRequest<{ attempts: TestAttempt[] }>('/api/tests/attempts', {}, authToken);
      applyUserPayload(mePayload.user);
      setAttempts((attemptsPayload.attempts || []) as TestAttempt[]);
      if (!silent) {
        setError(null);
      }
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load user data');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [applyUserPayload]);

  const runForegroundSync = useCallback(async (authToken: string) => {
    if (!authToken) return;
    if (syncInFlightRef.current) {
      syncQueuedRef.current = true;
      return;
    }

    syncInFlightRef.current = true;
    try {
      await loadMcqData().catch(() => undefined);
      if (user) {
        await loadUserData(authToken, true).catch(() => undefined);
      }
    } finally {
      syncInFlightRef.current = false;
      if (syncQueuedRef.current) {
        syncQueuedRef.current = false;
        void runForegroundSync(authToken);
      }
    }
  }, [loadMcqData, loadUserData, user]);

  const scheduleDebouncedForegroundSync = useCallback(
    (authToken: string) => {
      if (!authToken) return;
      if (syncDebounceTimerRef.current) {
        window.clearTimeout(syncDebounceTimerRef.current);
      }
      syncDebounceTimerRef.current = window.setTimeout(() => {
        syncDebounceTimerRef.current = null;
        void runForegroundSync(authToken);
      }, 420);
    },
    [runForegroundSync],
  );

  useEffect(() => {
    let cancelled = false;

    if (authLoading) {
      return () => {
        cancelled = true;
      };
    }

    if (!user) {
      setMcqs([]);
      setMcqTotalsBySubject(
        Object.fromEntries(SUBJECT_KEYS.map((subject) => [subject, 0])) as Record<SubjectKey, number>,
      );
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    void loadMcqData().catch((err) => {
      if (!cancelled) {
        const message = err instanceof Error ? err.message : 'Could not load MCQ dataset';
        setError(message);
        setMcqs([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [authLoading, user, loadMcqData]);

  useEffect(() => {
    if (!user) {
      setAttempts([]);
      setProfile(defaultProfile);
      setPreferences(defaultPreferences);
      setLoading(false);
      return;
    }

    const authToken = resolveClientAuthToken();
    if (!authToken) return;
    void loadUserData(authToken).catch(() => undefined);
  }, [token, user?.id, loadUserData, resolveClientAuthToken]);

  useEffect(() => {
    const authToken = resolveClientAuthToken();
    if (!authToken) return;

    const onDataSync = () => {
      if (!document.hidden) scheduleDebouncedForegroundSync(authToken);
    };
    window.addEventListener('net360:data-sync', onDataSync);

    // The browser EventSource to Railway is a long-lived HTTP response. On the
    // production HTTP/2 edge it resets the connection (ERR_HTTP2_PROTOCOL_ERROR)
    // and the next calls, including profile, are reported as CORS failures.
    // Web realtime already arrives on the websocket. Keep EventSource for native.
    if (!isNativeRuntime()) {
      return () => {
        window.removeEventListener('net360:data-sync', onDataSync);
        if (syncDebounceTimerRef.current) {
          window.clearTimeout(syncDebounceTimerRef.current);
          syncDebounceTimerRef.current = null;
        }
      };
    }

    let closed = false;
    let reconnectTimer: number | null = null;
    let source: EventSource | null = null;
    let reconnectDelay = 1500;
    // `/api/stream` answers 401 (no `open` event) when the request carries no usable credential —
    // e.g. the browser blocks the cross-site auth cookie (EventSource cannot send a bearer
    // header and the API ignores `?token=` in production). Retrying that forever only produced a
    // stream of 401s, so give up after a few consecutive pre-open failures and try again on the
    // next token change / tab focus / network resume. Socket.IO carries realtime events anyway.
    let opened = false;
    let consecutivePreOpenFailures = 0;
    let pausedLogged = false;

    const closeCurrent = () => {
      if (source) {
        source.close();
        source = null;
      }
    };

    const connect = () => {
      if (closed) return;
      closeCurrent();
      opened = false;

      source = new EventSource(buildSseStreamUrl(authToken), { withCredentials: true });

      source.onopen = () => {
        logNativeEvent('socket', 'appdata-stream-open');
        opened = true;
        consecutivePreOpenFailures = 0;
        pausedLogged = false;
        reconnectDelay = 1500;
      };

      source.addEventListener('sync', (evt: MessageEvent) => {
        try {
          const data = JSON.parse(String(evt.data || '{}')) as { type?: string; previousSessionId?: string };
          if (data?.type === 'auth.session_revoked' && data?.previousSessionId) {
            window.dispatchEvent(new CustomEvent('net360:session-revoked', { detail: data }));
            return;
          }
          if (data?.type === 'subscription.refresh') {
            window.dispatchEvent(new CustomEvent('net360:subscription-refresh', { detail: data }));
            return;
          }
        } catch {
          /* fall through */
        }
        if (!document.hidden) {
          scheduleDebouncedForegroundSync(authToken);
        }
      });

      source.addEventListener('heartbeat', () => {
        // Keeps stream warm; no action required.
      });

      source.onerror = () => {
        logNativeEvent('socket', 'appdata-stream-error', { reconnectDelay }, 'warn');
        if (!opened) consecutivePreOpenFailures += 1;
        closeCurrent();
        if (closed) return;
        if (consecutivePreOpenFailures >= 3) {
          if (!pausedLogged) {
            pausedLogged = true;
            console.warn('[stream] /api/stream rejected 3 times in a row; pausing until the session token changes or the tab regains focus.');
          }
          return;
        }
        reconnectTimer = window.setTimeout(() => {
          reconnectTimer = null;
          connect();
        }, reconnectDelay);
        reconnectDelay = Math.min(Math.round(reconnectDelay * 1.65), 15000);
      };
    };

    // Resume a paused stream (see above) when the tab becomes visible or the network returns.
    const resumeIfPaused = () => {
      if (closed || source || reconnectTimer != null || document.hidden) return;
      if (consecutivePreOpenFailures < 3) return;
      consecutivePreOpenFailures = 0;
      reconnectDelay = 1500;
      connect();
    };
    document.addEventListener('visibilitychange', resumeIfPaused);
    window.addEventListener('online', resumeIfPaused);

    connect();

    return () => {
      closed = true;
      window.removeEventListener('net360:data-sync', onDataSync);
      document.removeEventListener('visibilitychange', resumeIfPaused);
      window.removeEventListener('online', resumeIfPaused);
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }
      if (syncDebounceTimerRef.current) {
        window.clearTimeout(syncDebounceTimerRef.current);
        syncDebounceTimerRef.current = null;
      }
      closeCurrent();
    };
  }, [token, runForegroundSync, resolveClientAuthToken, scheduleDebouncedForegroundSync]);

  useEffect(() => {
    if (!user) return;
    const authToken = resolveClientAuthToken();
    if (!authToken) return;

    const onVisibility = () => {
      if (document.hidden) {
        markNativeDocumentHidden();
        return;
      }
      if (consumeBriefNativeHide()) return;
      scheduleDebouncedForegroundSync(authToken);
    };

    const onOnline = () => {
      logNativeEvent('socket', 'appdata-online-resync');
      scheduleDebouncedForegroundSync(authToken);
    };

    const onFocus = () => {
      if (document.hidden) return;
      if (consumeBriefNativeHide()) return;
      scheduleDebouncedForegroundSync(authToken);
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onFocus);
      if (syncDebounceTimerRef.current) {
        window.clearTimeout(syncDebounceTimerRef.current);
        syncDebounceTimerRef.current = null;
      }
    };
  }, [token, user, scheduleDebouncedForegroundSync, resolveClientAuthToken]);

  const mcqsBySubject = useMemo(() => {
    const grouped = Object.fromEntries(SUBJECT_KEYS.map((subject) => [subject, [] as MCQ[]])) as Record<SubjectKey, MCQ[]>;

    mcqs.forEach((question) => {
      const subjectKey = normalizeMcqSubjectKey(String(question?.subject || ''));
      if (!subjectKey || !Object.prototype.hasOwnProperty.call(grouped, subjectKey)) return;
      grouped[subjectKey].push(question);
    });

    return grouped;
  }, [mcqs]);

  const mcqsBySubjectAndDifficulty = useMemo(() => {
    const grouped = Object.fromEntries(
      SUBJECT_KEYS.map((subject) => [subject, { Easy: [] as MCQ[], Medium: [] as MCQ[], Hard: [] as MCQ[] }]),
    ) as Record<SubjectKey, Record<Difficulty, MCQ[]>>;

    mcqs.forEach((question) => {
      const subjectKey = normalizeMcqSubjectKey(String(question?.subject || ''));
      if (!subjectKey || !Object.prototype.hasOwnProperty.call(grouped, subjectKey)) return;
      const subjectBuckets = grouped[subjectKey];
      const difficultyKey = normalizeMcqDifficulty(String(question?.difficulty || ''));
      const targetBucket = subjectBuckets?.[difficultyKey];
      if (!Array.isArray(targetBucket)) return;
      targetBucket.push(question);
    });

    return grouped;
  }, [mcqs]);

  const refreshAttempts = async () => {
    const authToken = resolveClientAuthToken();
    if (!authToken) return;
    const payload = await apiRequest<{ attempts: TestAttempt[] }>('/api/tests/attempts', {}, authToken);
    setAttempts((payload.attempts || []) as TestAttempt[]);
  };

  const startTestSession: AppDataContextValue['startTestSession'] = async ({
    subject,
    difficulty,
    topic,
    mode,
    questionCount = 20,
    part,
    chapter,
    section,
    netType,
    testType,
    selectedSubject,
    authTokenHint,
  }) => {
    const trimmedHint =
      typeof authTokenHint === 'string' && authTokenHint.trim() ? authTokenHint.trim() : null;
    const storedAccess = readPersistedStudentAccessToken();
    let authToken: string | null =
      trimmedHint && !isCookieSessionApiMarker(trimmedHint)
        ? trimmedHint
        : storedAccess && !isCookieSessionApiMarker(storedAccess)
          ? storedAccess
          : resolveClientAuthToken();

    if (!authToken || isCookieSessionApiMarker(authToken)) {
      await waitUntilAuthHydrated(() => authLoadingRef.current);
      if (!readPersistedStudentAccessToken() && !token && !user) {
        throw new Error('Please login first to start a server-backed test session.');
      }
      await waitUntilClientAuthToken(resolveClientAuthToken);
      authToken = resolveClientAuthToken();
      if (!authToken || isCookieSessionApiMarker(authToken)) {
        authToken = await resolveLaunchAuthToken(token);
      }
      if (!authToken) {
        throw new Error('Please login first to start a server-backed test session.');
      }
      await ensureStudentBearerTokenFromRefresh(token);
      const bearerAfterPrime = readPersistedStudentAccessToken();
      if (bearerAfterPrime && !isCookieSessionApiMarker(bearerAfterPrime)) {
        authToken = bearerAfterPrime;
      }
    }

    if (import.meta.env.DEV) {
      console.log('Token before request:', formatStudentTokenDebugPreview());
    }

    const normalizedPayload = {
      subject: String(subject || '').toLowerCase().trim(),
      chapter: String(chapter || '').trim(),
      topic: String(topic || '').trim(),
      section: String(section || '').trim(),
      difficulty: String(difficulty || '').trim(),
      mode,
      questionCount,
      part: String(part || '').toLowerCase().trim(),
      netType,
      testType,
      selectedSubject: String(selectedSubject || '').toLowerCase().trim(),
    };
    if (import.meta.env.DEV) {
      console.log('[MCQ Test Request] Sending:', normalizedPayload);
    }

    const startPayload = await apiRequest<{ session: TestSession }>(
      '/api/tests/start',
      {
        method: 'POST',
        retryCount: 0,
        timeoutMs: 20_000,
        body: JSON.stringify(normalizedPayload),
      },
      authToken,
    );
    if (import.meta.env.DEV) {
      console.log('[MCQ Test Response]', {
        subject: normalizedPayload.subject,
        chapter: normalizedPayload.chapter,
        topic: normalizedPayload.topic,
        section: normalizedPayload.section,
        returnedMcqs: Array.isArray(startPayload?.session?.questions) ? startPayload.session.questions.length : 0,
      });
    }
    logNativeEvent('practice-board', 'test-session-start-success', {
      subject: normalizedPayload.subject,
      chapter: normalizedPayload.chapter,
      topic: normalizedPayload.topic,
      mode: normalizedPayload.mode,
      questionCount: normalizedPayload.questionCount,
      returnedMcqs: Array.isArray(startPayload?.session?.questions) ? startPayload.session.questions.length : 0,
    });
    cacheLaunchedExamSession(startPayload.session);
    return startPayload.session;
  };

  const getTestSession: AppDataContextValue['getTestSession'] = async (sessionId) => {
    const authToken = resolveClientAuthToken();
    if (!authToken) {
      throw new Error('Please login first to load a test session.');
    }

    const payload = await apiRequest<{ session: TestSession }>(`/api/tests/${sessionId}`, {}, authToken);
    return payload.session;
  };

  const submitTestSession: AppDataContextValue['submitTestSession'] = async ({ sessionId, answers, elapsedSeconds }) => {
    const authToken = resolveClientAuthToken();
    if (!authToken) {
      throw new Error('Please login first to submit a test session.');
    }

    const payload = await apiRequest<{ attempt: TestAttempt; review?: TestReviewRow[] }>(
      `/api/tests/${sessionId}/finish`,
      {
        method: 'POST',
        body: JSON.stringify({ answers, elapsedSeconds }),
      },
      authToken,
    );

    const attempt = payload.attempt;
    setAttempts((previous) => {
      const withoutSame = previous.filter((item) => item.id !== attempt.id);
      return [attempt, ...withoutSame];
    });
    return {
      attempt,
      review: Array.isArray(payload.review) ? payload.review : [],
    };
  };

  const startPracticeTest: AppDataContextValue['startPracticeTest'] = async ({
    subject,
    difficulty,
    topic,
    mode,
    questionCount = 20,
  }) => {
    const session = await startTestSession({ subject, difficulty, topic, mode, questionCount });

    // Backward-compatible quick mode for existing topic/adaptive cards: select first option per question.
    const answers = session.questions.map((question) => ({
      questionId: question.id,
      selectedOption: question.options[0] || null,
    }));

    const { attempt } = await submitTestSession({
      sessionId: session.id,
      answers,
      elapsedSeconds: Math.max(60, Math.round(session.durationMinutes * 60 * 0.6)),
    });

    return attempt;
  };

  const saveProfile = async (partial: Partial<ProfileState>) => {
    const authToken = resolveClientAuthToken();
    if (!authToken || !user) {
      throw new Error('Please login first to save profile.');
    }

    const payload = await apiRequest<{ user: ProfileState & { preferences: PreferencesState } }>(
      '/api/auth/profile',
      {
        method: 'PUT',
        body: JSON.stringify(partial),
      },
      authToken,
    );

    setProfile({
      firstName: payload.user.firstName || '',
      lastName: payload.user.lastName || '',
      email: payload.user.email || '',
      phone: payload.user.phone || '',
      city: payload.user.city || '',
      targetProgram: payload.user.targetProgram || '',
      testSeries: payload.user.testSeries || '',
      sscPercentage: payload.user.sscPercentage || '',
      hsscPercentage: payload.user.hsscPercentage || '',
      testDate: payload.user.testDate || '',
    });
  };

  const savePreferences = async (partial: Partial<PreferencesState>) => {
    const authToken = resolveClientAuthToken();
    if (!authToken || !user) {
      throw new Error('Please login first to save preferences.');
    }

    const payload = await apiRequest<{ user: ProfileState & { preferences: PreferencesState } }>(
      '/api/auth/preferences',
      {
        method: 'PUT',
        body: JSON.stringify(partial),
      },
      authToken,
    );

    setPreferences(normalizeSharedPreferences(payload.user.preferences || defaultPreferences));
  };

  return (
    <AppDataContext.Provider
      value={{
        loading,
        error,
        mcqs,
        mcqTotalsBySubject,
        mcqsBySubject,
        mcqsBySubjectAndDifficulty,
        attempts,
        profile,
        preferences,
        startPracticeTest,
        startTestSession,
        getTestSession,
        submitTestSession,
        saveProfile,
        savePreferences,
        refreshAttempts,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used inside AppDataProvider');
  }
  return context;
}
